import { useCallback, useEffect, useMemo, useState } from 'react';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { usePermission } from '~/app/auth/hooks/usePermission';
import type { RoleDetailDto } from '~/app/services/authorization/role.service';
import { RoleService } from '~/app/services/authorization/role.service';
import { isRoleNameEditable } from '../utils';
import { resolveAuthorizationErrorMessage } from '../utils/resolve-authorization-error';

const MAX_ROLE_NAME_LENGTH = 100;

type UseRoleNameEditorOptions = {
  role: RoleDetailDto | null;
  organizationId: string | null;
  useSystemRoleApi: boolean;
  onSaved: () => void;
};

export function useRoleNameEditor({
  role,
  organizationId,
  useSystemRoleApi,
  onSaved,
}: UseRoleNameEditorOptions) {
  const { t } = useTranslation('authorization');
  const { message } = App.useApp();
  const { hasPermission } = usePermission();

  const [savedName, setSavedName] = useState('');
  const [draftName, setDraftName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  useEffect(() => {
    const name = role?.name ?? '';
    setSavedName(name);
    setDraftName(name);
  }, [role?.id, role?.name]);

  const canEditName = useMemo(
    () =>
      Boolean(
        role &&
        organizationId &&
        !useSystemRoleApi &&
        isRoleNameEditable(role) &&
        hasPermission(PermissionKey.ROLES_UPDATE),
      ),
    [hasPermission, organizationId, role, useSystemRoleApi],
  );

  const isNameDirty = useMemo(
    () => canEditName && draftName.trim() !== savedName.trim(),
    [canEditName, draftName, savedName],
  );

  const onNameChange = useCallback((value: string) => {
    setDraftName(value);
  }, []);

  const onDiscardName = useCallback(() => {
    setDraftName(savedName);
  }, [savedName]);

  const onSaveName = useCallback(async () => {
    if (!role || !organizationId || !isNameDirty) return;

    const trimmedName = draftName.trim();
    if (!trimmedName) {
      message.error(t('createModal.nameRequired'));
      return;
    }

    if (trimmedName.length > MAX_ROLE_NAME_LENGTH) {
      message.error(t('createModal.nameMaxLength'));
      return;
    }

    setIsSavingName(true);
    try {
      await RoleService.updateRole(organizationId, role.id, trimmedName);
      setSavedName(trimmedName);
      setDraftName(trimmedName);
      message.success(t('messages.roleNameSaveSuccess'));
      onSaved();
    } catch (error) {
      message.error(
        resolveAuthorizationErrorMessage(
          error,
          t,
          'messages.roleNameSaveError',
        ),
      );
    } finally {
      setIsSavingName(false);
    }
  }, [draftName, isNameDirty, message, onSaved, organizationId, role, t]);

  return {
    canEditName,
    draftName,
    isNameDirty,
    isSavingName,
    onNameChange,
    onDiscardName,
    onSaveName,
  };
}
